// MUSI byc pierwszym importem — self-install shim requestIdleCallback zanim
// jakikolwiek inny modul (app/biblioteka) go uzyje na Safari (issue #234).
import "@/lib/request-idle-callback-polyfill";
import { routeTree } from "virtual:dev-podglad-routes";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/auth-api";
import {
	clearStaleReloadMarker,
	isDynamicImportChunkError,
	reloadOnceAfterDynamicImportFailure,
} from "@/lib/dynamic-import";
import { DEFAULT_QUERY_OPTIONS } from "@/lib/query-client-defaults";
import { hydratePersistedQueryCache, subscribePersistedQueryCache } from "@/lib/query-persist";
import { redactQueryKey } from "@/lib/redact-query-key";
import { initSentry, Sentry, shouldCaptureQueryError } from "@/lib/sentry";
import { applyUiScale, readUiScale } from "@/lib/ui-scale";
import "@/styles/globals.css";

initSentry();
// Przed pierwszym renderem, zeby uklad telefonu nie przeskakiwal po starcie.
applyUiScale(readUiScale());

const queryClient = new QueryClient({
	defaultOptions: { queries: DEFAULT_QUERY_OPTIONS },
	queryCache: new QueryCache({
		onError: (error, query) => {
			if (error instanceof ApiError && error.status === 404) {
				return;
			}
			if (!shouldCaptureQueryError(error)) {
				return;
			}
			// Klucz idzie do zewnetrznego serwisu, wiec przechodzi przez redakcje —
			// druga warstwa na wypadek, gdyby ktos wstawil do niego sekret (F20).
			const safeKey = redactQueryKey(query.queryKey);
			Sentry.captureException(error, {
				tags: { queryKey: JSON.stringify(safeKey) },
			});
			// Background refetch failure — data already cached, toast instead of breaking UI
			if (query.state.data !== undefined) {
				console.error("[query] Background refresh failed:", error.message, safeKey);
			}
		},
	}),
});

// PRZED `createRouter` i pierwszym renderem: loader trasy robi prefetch przez
// `ensureQueryData`, więc cache musi być gotowy zanim ktokolwiek o niego zapyta.
if (typeof window !== "undefined") {
	hydratePersistedQueryCache(queryClient);
	subscribePersistedQueryCache(queryClient);
}

const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	context: { queryClient },
	// Z autoCodeSplitting komponent route'u może wymagać dociągnięcia chunka —
	// po 200 ms bez odpowiedzi pokazujemy spinner (min 300 ms, bez migotania).
	defaultPendingComponent: RoutePendingFallback,
	defaultPendingMs: 200,
	defaultPendingMinMs: 300,
});

function RoutePendingFallback() {
	return (
		<div className="flex h-dvh items-center justify-center">
			<div className="size-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
		</div>
	);
}

if (typeof window !== "undefined") {
	clearStaleReloadMarker();

	window.addEventListener("vite:preloadError", (event) => {
		event.preventDefault();
		reloadOnceAfterDynamicImportFailure();
	});

	// Belt-and-braces: per-callsite try/catch in the app calls
	// `attemptDynamicImportRecovery` directly, but if any new code path
	// forgets to do that, this listener still catches the rejection.
	window.addEventListener("unhandledrejection", (event) => {
		if (!isDynamicImportChunkError(event.reason)) return;
		event.preventDefault();
		reloadOnceAfterDynamicImportFailure();
	});
}

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

// biome-ignore lint/style/noNonNullAssertion: root element always present
createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<TooltipProvider delayDuration={200}>
				<RouterProvider router={router} />
				<Toaster richColors position="bottom-right" />
			</TooltipProvider>
		</QueryClientProvider>
	</StrictMode>,
);
